/* eslint-disable no-secrets/no-secrets */
/* eslint-disable padding-line-between-statements */

import { useCallback, useState, useContext } from 'react';
import _ from 'lodash';
import moment from 'moment';
import  { TwiaBillingService }  from 'ent-capability-policybilling';
import { useModal } from '@jutro/components';
import { useTranslator, IntlContext } from '@jutro/locale';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import messages from 'ent-capability-billing-react/ManagePaymentMethodsPage/ManagePaymentMethodsPage.messages';
import makeAPaymentMessages from 'ent-capability-billing-react/MakeAPayment/MakeAPaymentComponent.messages';
import { PolicyService } from 'gw-capability-policy';
import ConfirmationPopup from "ent-capability-billing-react/ConfirmationPopup/ConfirmationPopup";
import DuplicatePaymentPopup  from "ent-capability-billing-react/DuplicatePaymentPopup/DuplicatePaymentPopup";
import appConfig from 'app-config';

function useAciUtil(){

    const translator = useTranslator()
    const [/* activeAPICall */, setActiveAPICall] = useState(false);
    const intlContext = useContext(IntlContext);

    const { showModal,showAlert,showConfirm } = useModal();

    const generateModalError = useCallback((messageHeader, messageDescription, withArgs) => {
        showAlert({
            title: translator(messageHeader),
            message: _.isEmpty(withArgs) || _.isUndefined(withArgs) ? translator(messageDescription) : translator(messageDescription, withArgs),
            status: 'error',
            icon: 'mi-error-outline',
            confirmButtonText: commonMessages.ok
        });
    }, [showAlert, translator]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const formatCurrency = useCallback(amount => 
        _.get(amount, 'amount') === undefined ||  _.get(amount, 'amount')=== null  ? translator(messages.tbd)
            : intlContext.formatNumber(amount.amount, {
                style: 'currency',
                currency: _.get(amount, 'currency')
            }) 
    );

    // 2025-01-28 - Debra Frum - US25425 - CE - Offer Edit - 10 Pay
    // Filter out all single use payment instruments when auto pay is selected.
    // Otherwise include the newly added single use payment instrument if one has been added.
    /**
     * @param paymentInstruments - Includes payment instruments saved for future use and the newly added single use 
     *                             if one has been added
     */
    const filterPaymentInstruments = (isAutoDraft, paymentInstruments, setFilteredPaymentInstruments) => {
        let filteredPaymentInstruments;

        // Filters out all single use payment instruments
        if (isAutoDraft) {
            filteredPaymentInstruments = paymentInstruments.filter(pi => pi.singleUse === false);
        // Filter out all single use payment instruments except for the newly added one
        } else {
            filteredPaymentInstruments = paymentInstruments;
        }
        setFilteredPaymentInstruments(filteredPaymentInstruments);
    };

    // 2025-01-23 - Debra Frum - DE18045 - Getting error popup after adding new payment method on Offer Details screen
    // To resolve ACI error resulting from multiple unnecessary retrievals of synced payment instruments,
    // lifted state of payment instruments from child to parent to be shared with all children
    /**
     * @param paymentInstruments - Includes payment instruments saved for future use and the  newly added single use
     *                             payment instrument if one was just added.  All previously added single use payment 
     *                             instrument are filtered out.
     */
    // eslint-disable-next-line consistent-return
    const getSyncedPaymentInstrumentsForAccount = useCallback(async (accountNumber, authHeader) => {
        if (!_.isEmpty(accountNumber) && authHeader) {
        const response = await TwiaBillingService.getSyncedPaymentInstruments(accountNumber, true, authHeader)
        let filteredResp;

        // Payment Instrument was added
        if (!_.isEmpty(window.location.search)) {
            // 2025-01-09 - Debra Frum - US25427 - CE - Make a Payment on Offer Details
            // Get new payment instrument returned from ACI Add Payment Method page
            let paymentInstrumentTokenId;
            const searchParams = new URLSearchParams(window.location.search);
            const tokenId = searchParams.get("tokenId");
            // Returning from ACI  - check if payment instrument is added.

            if (!_.isNil(tokenId)) {
                paymentInstrumentTokenId = tokenId;
            }

            // Filter out all single use payment instruments except for the newly added one if it was single use
            filteredResp = response.filter(pi => pi.singleUse === false  || pi.token === paymentInstrumentTokenId);

        // Payment Instrument wasn't added
        } else {
            // Filter out all single use payment instruments
            filteredResp = response.filter(pi => pi.singleUse === false);
        }
        
        return _.sortBy(filteredResp, ['description', 'detail']);
    }
    }, []);

    const editPaymentInstrumentForAccount = useCallback((payIns, currState, authHeader) => {
        TwiaBillingService.getEditPaymentInstrumentSessionTransfer(payIns.accountNumber, 'PAYMENT_METHODS', 'Homeowners', payIns.token, currState, authHeader)
        .then((url) => {
            window.location = url;
        }).catch(e => {
           setActiveAPICall(false);
           
            const errorCode = _.get(e, 'baseError.error.data.appErrorCode');

            if (errorCode === 10007) {
                // DE12503 - ACI undergoing maintenance - Exception Handling
                generateModalError(messages.paymentSystemUnavailable, messages.paymentSystemUnavailableBody);
            } else {
                generateModalError( messages.editPaymentMethod,   messages.editPaymentGeneralError );
            }
        });
    }, [generateModalError]);

    const addPaymentInstrumentsToAccount = useCallback(config => {
        const { 
            selectedAccountNumber, 
            identifier,
            streamPublicId,
            onComplete = undefined, 
            method, 
            lob = 'Homeowners', 
            fullName = undefined, 
            emailAddress = undefined,
            useProfile,
            currState, 
            authHeader
        } = config;

        TwiaBillingService.canAddPaymentInstrumentsToAccount(selectedAccountNumber, authHeader)
        .then((res) => {
            let identifierByFunc;

            if (!res) {
                generateModalError(messages.savedPaymentMethodLimitTitle, messages.maxAllowedAPIReached);               
            } else if (!_.isUndefined(selectedAccountNumber)){
                if (config.payFrom === 'setAutoPay'){
                    identifierByFunc = streamPublicId;
                }
                else {
                    identifierByFunc = identifier;
                }
                
                TwiaBillingService.getPaymentInstrumentSessionTransfer( identifierByFunc, method, lob, useProfile, currState, fullName, emailAddress, authHeader)
                .then((url) => {window.location = url;})
                .catch(e => {
                    // if the window does not open as expected
                    setActiveAPICall(false);
    
                    const errorCode = _.get(e, 'baseError.error.data.appErrorCode');
    
                    if (errorCode === 10006) {
                        generateModalError(messages.maxAllowedAPIReached, messages.showACIMaxPIReachedMessage);
                    } else if (errorCode === 10007) {
                        generateModalError(messages.paymentSystemUnavailable, messages.paymentSystemUnavailableBody);
                    } else {
                        generateModalError(messages.unableToAddPaymentMethodTitle, messages.unableToAddPaymentMethodBody);
                    }
                })
                .finally(() => {
                    // eslint-disable-next-line no-unused-expressions
                    onComplete && onComplete();
                });
            }
        });
    }, [generateModalError]);

    // 2025-01-14 - Debra Frum - US25428 - CE - Offer Bind and Confirmation
    // Determines if payment is being made on an offer or renewal
    const getPeriodDetailsForPayment = useCallback((policyCommonDetails) => !_.isNil(policyCommonDetails.renewedPeriodDetails)
            ? policyCommonDetails.renewedPeriodDetails 
            : policyCommonDetails.currentPeriodDetails, [])
    

    const makeOfferPayment = useCallback(async (config, overrideDupPayment) => {
        const {policyCommonDetails,setLoading,authHeader,onClickOk,onResolve,onReject,onAfterClose} = config;

        // 2025-01-23 - Debra Frum - US25425 - CE - Offer Edit - 10 Pay
        // If automatic payments have been selected, confirm authorization of draft from user's account
        const isAutoPayEnabled = _.get(policyCommonDetails, 'bindData.customerPaymentDetails_TWIA.isAutoPayEnabled');

        if (isAutoPayEnabled) {
            const confirmAutoPayDialogPromise = {
                title: translator(makeAPaymentMessages.confirmAutoPayTitle),
                message: translator(makeAPaymentMessages.confirmAutoPayDescription),
                status: 'info',
                icon: 'mi-info-outline',
                confirmButtonText: commonMessages.ok,
                cancelButtonText: commonMessages.cancelModel,
            };
            
            const result = await showConfirm(confirmAutoPayDialogPromise);

            if (result !== 'confirm') {
                return;
            }
        }

        const offerNumber = getPeriodDetailsForPayment(policyCommonDetails).jobNumber_TWIA;
        
        setLoading(true);

        try{
            const response = await PolicyService.customerPayAndBind_TWIA(offerNumber, policyCommonDetails.bindData, overrideDupPayment , authHeader);

            if (response.isPaymentExistInACI && response.lastPaymentDate) {

                const componentProps = {                
                    paymentDetails: response,
                    authHeader,
                    isOpen: true,
                    currency:'usd',
                    onResolve,
                    onReject,
                    onAfterClose,
                    config,
                    makeOfferPayment,
                }

                // eslint-disable-next-line react/react-in-jsx-scope
                showModal(<DuplicatePaymentPopup {...componentProps} />);
            }

            setLoading(false);
        
            if(response?.bindData_TWIA?.customerPaymentDetails_TWIA?.confirmationNumber) {
                const paymentDetails = response.bindData_TWIA.customerPaymentDetails_TWIA;
                const policyData =  response;

                // 2025-01-20 - Debra Frum - US25428 - CE - Offer Bind and Confirmation
    			// Pass policy data for new Offers to display the Submission Successful page

                // eslint-disable-next-line react/react-in-jsx-scope
                showModal(<ConfirmationPopup 
                                paymentDetails={paymentDetails} 
                                currency='usd'
                                policyData={policyData} 
                                onResolve={onResolve} 
                                payFrom ="offers" 
                                onClickOk={onClickOk} 
                            />)
            }
        }
        catch(e){
            const errorCode = e?.data?.appErrorCode
            
            const {amount} = policyCommonDetails.bindData.customerPaymentDetails_TWIA;

            if (errorCode === 10070) { // unable to issue error
                generateModalError(messages.paymentCannotProcessed, messages.pleaseTryAgain);
            } else if (errorCode === 10071) { // declined error
                generateModalError(messages.yourCardwasDeclinedTitle, messages.yourCardwasDeclinedBody);
            } else if (errorCode === 10073) { // duplicate payment error 
                generateModalError(messages.duplicatePaymentErrorTitle, messages.duplicatePaymentErrorBody, {amount: formatCurrency({ currency: 'usd', amount })});
            } else if (errorCode === 10080) {
                // US29148 redirect on throttle to stop any javascript 
                window.location.href =  appConfig.twiaerrorPageApplication;                
            } else { // general error
                generateModalError(messages.unableToCompletePaymentTitle, messages.unableToCompletePaymentBody);
            }
    
            setLoading(false);    
        }
    }, [formatCurrency, generateModalError, getPeriodDetailsForPayment, showConfirm, showModal, translator])

    const displayExpirationDate = (accountType, expirationDate) =>{

        const accountTypes = ['PERSONAL CHECKING', 'PERSONAL SAVINGS', 'BUSINESS CHECKING', 'BUSINESS SAVINGS'];

        if (accountTypes.includes(accountType) ){
            return undefined
        } 
            
        return moment(expirationDate, 'MMDDYY').toDate()
    }

    const createOrUpdatePaymentInstrument = useCallback((searchParams, history, authHeader) =>{
        const ssoKey = searchParams.get("ssoKey");
        const status = searchParams.get("status");

        if (ssoKey !== null && status !== null) {
            
            const  returnedAccountNumber = searchParams.get("accountNumber");    

            if (status==="Success") {
                   
                const securityStamp = searchParams.get("securityStamp");
                
                const fundingAccountSubType = searchParams.get("fundingAccountSubType");
                const tokenId = searchParams.get("tokenId");
                const expiryDate = displayExpirationDate(fundingAccountSubType, searchParams.get("expiryDate")); 
                const singleUse= searchParams.get("singleUse") === 'Y';
                const description = searchParams.get("nickName");
                const detail = searchParams.get("fundingAccount");
                const aState = searchParams.get("state");

                const handleError = () => {
                    // console.error(`unable to create payment method: "${e}"`); 
                   generateModalError( messages.unableToAddPaymentMethodTitle, messages.unableToAddPaymentMethodBody);
               };
               
                TwiaBillingService.saveFundingPortalResponse(returnedAccountNumber, JSON.stringify(searchParams.toString()), authHeader);
            
                const newPI = {
                    'token': tokenId,
                    'expirationDate': expiryDate,
                    'singleUse': singleUse,
                    'description': description,
                    'detail': detail 
                };

                let bState;

                if (aState != null) {
                    bState = JSON.parse(window.atob(window.decodeURIComponent(aState)));

                    newPI.accountNumber = bState.accountNumber;

                    if (bState.action === 'new') {                
                        TwiaBillingService.createPaymentInstrument(securityStamp, fundingAccountSubType, newPI, authHeader)
                        .then(history.push('/manage-payment-methods'))
                        .catch(handleError);
                    } else if (bState.action === 'edit') {
                        TwiaBillingService.updatePaymentInstrument(securityStamp, fundingAccountSubType, newPI, authHeader)
                        .then( history.push('/manage-payment-methods'))
                        .catch(handleError);
                    }
                }            
            }  else if (status === "Failure"){

                const reason = searchParams.get("reason");
                const ErrMessage = `Unable to create payment method: "${reason}" on account ${returnedAccountNumber}`;
            
                if (reason !== 'Consumer Cancellation') {
                    generateModalError( messages.unableToAddPaymentMethodTitle, messages.unableToAddPaymentMethodBody);
                    TwiaBillingService.logErrorMessage(ErrMessage, authHeader);
                 }
            }
        }
    }, [generateModalError]);
 
    // 2025-03-03 - Debra Frum - DE18428 - DR01- CE - Payment method is not saved when auto-pay is selected for
    //                                     payment plans other than 10 pay
    // If an error occurs when a payment instrument is added on the ACI Payment Instrument page, display an error
    // message popup and save the error returned from ACI page to the BC log file
    const handleAddPaymentInstrumentError = useCallback((accountNumber, offerNumber, searchParams, authHeader) => {
        // Returning from ACI Payment Instrument page?
        if (searchParams) {
            const ssoKey = searchParams.get("ssoKey");
            const status = searchParams.get("status");

            if (ssoKey !== null && status !== null) {
                TwiaBillingService.saveFundingPortalResponse(accountNumber, JSON.stringify(searchParams.toString()), authHeader);
                if (status !== "Success") {
                    const reason = searchParams.get("reason");
                
                    // US18915 popup and BC log message for errors
                    if (reason) {
                        const message = `Unable to create payment method: "${reason}" on account ${accountNumber} and offer number: ${offerNumber}`;
                        // console.error(message);
                        if (reason !== 'Consumer Cancellation') {
                            generateModalError( messages.unableToAddPaymentMethodTitle, messages.unableToAddPaymentMethodBody);
                            TwiaBillingService.logErrorMessage(message, authHeader);
                        }
                    }
                }
            }
        }
    }, [generateModalError]);

    return {
        filterPaymentInstruments,
        getSyncedPaymentInstrumentsForAccount,
        addPaymentInstrumentsToAccount,
        editPaymentInstrumentForAccount,
        generateModalError,
        createOrUpdatePaymentInstrument,
        makeOfferPayment,
        handleAddPaymentInstrumentError
    };
}

export default useAciUtil
